/**
 * The menu figure of the office queue — To do, mine and unhandled — kept fresh by polling while
 * the tab is visible and by a refetch when it becomes visible again.
 */
import { useQuery } from '@apollo/client/react';
import { useEffect } from 'react';
import { MasterAccountQueueSummaryDocument } from '@/graphql';

/** How often the menu entry re-reads the count while the tab stays open. */
export const QUEUE_SUMMARY_POLL_MS = 60_000;

interface UseMasterAccountQueueSummaryOptions {
  /** False for a caller who does not see the queue; nothing is queried then. */
  enabled: boolean;
}

/** Documents to do that are mine or nobody's; 0 until the first answer. */
export function useMasterAccountQueueSummary({
  enabled
}: UseMasterAccountQueueSummaryOptions): number {
  const { data, refetch } = useQuery(MasterAccountQueueSummaryDocument, {
    skip: !enabled,
    pollInterval: QUEUE_SUMMARY_POLL_MS,
    // A hidden tab is not looked at; the visibility refetch below catches up once it is.
    skipPollAttempt: () => document.visibilityState === 'hidden',
    fetchPolicy: 'cache-and-network'
  });

  useEffect(() => {
    if (!enabled) return;
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      // A failed refresh keeps the last count; the next poll tries again.
      refetch().catch(() => undefined);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [enabled, refetch]);

  return enabled && data ? data.masterAccountQueueSummary.toDoMineAndUnhandled : 0;
}
